import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { IJobScreen } from '../interfaces/job/job';
import { IJobFilter } from '../interfaces/job/job-filter';
import { ISearchSupplier } from '../interfaces/search-supplier';
import { EditService } from './edit.service';
import { HttpResponsesHandlersFactory } from '../handlers/http-responses-handler';
import { IProfileJobInfo } from '../interfaces/telephony/profile-job-info';
import { SelectItem } from 'primeng';
import { IInteractionChannel } from '../interfaces/interactions/interaction-channel';
import { IInteractionJobRef } from '../interfaces/interactions/interaction-job-ref';
import { ICustomer } from '../interfaces/customer';
import { IBasicJobData } from '../interfaces/telephony/basic-job-data.dto.interface';
import { ITaskTrackingDTO } from '../interfaces/task-tracking-info';
import { Operation } from 'fast-json-patch';
import { JobSupplierScheduleContext } from '../components/task-supplier-schedule/task-supplier-schedule';
import { JobScreenCarryOverTaskCheck } from '../interfaces/screen-specific/job-screen';
import { ChangeGrabDisabledStateDto } from '../interfaces/job/change-grab-disabled-state-dto';
import { TravelSchedulerDTO } from '../interfaces/travel-scheduler/travel-scheduler-task';

export interface IRemoveSupplier {
  jobNumber: string;
  supplierId?: number;
  id: number;
  jobUnassignedReasonId: number;
  jobUnassignedComment: string;
}

@Injectable({ providedIn: 'root' })
export class JobsService extends EditService<IJobScreen, IJobFilter> {
  url = '/api/job';
  entityTitle = 'Job';

  constructor(http: HttpClient, handlersFactory: HttpResponsesHandlersFactory) {
    super(http, handlersFactory);
  }

  public getInteractionJobRefs(): Promise<IInteractionJobRef[]> {
    const url = `${this.url}/interactionJobRefs`;
    return this.handleRequest(
      this.http.get<IInteractionJobRef[]>(`${url}`),
      this.handlersFactory.getDefault(),
    );
  }

  public getInteractionJobRef(jobId): Promise<IInteractionJobRef> {
    const url = `${this.url}/interactionJobRef/${jobId}`;
    return this.handleRequest(
      this.http.get<IInteractionJobRef>(`${url}`),
      this.handlersFactory.getDefault(),
    );
  }

  public getInteractionSuggestedJobs(supplierId): Promise<IInteractionJobRef[]> {
    const url = `${this.url}/suggestedJobs/${supplierId}`;
    return this.handleRequest(
      this.http.get<IInteractionJobRef[]>(`${url}`),
      this.handlersFactory.getDefault(),
    );
  }

  public changeSupplierPriceAccount(item: any): Promise<any> {
    return this.handleRequest(
      this.http.post(this.url + '/changeSupplierPriceAccount', item),
      this.handlersFactory.getForUpdate(this.entityTitle),
    ).then(this.extractData);
  }

  public changeSupplierDetails(item: any): Promise<any> {
    return this.handleRequest(
      this.http.post(this.url + '/changeSupplierDetails', item),
      this.handlersFactory.getForUpdate(this.entityTitle),
    ).then(this.extractData);
  }

  public sendJobAlert(jobId: number, supplierIds: number[]) {
    let suppliers = encodeURIComponent(JSON.stringify(supplierIds));
    return this.http
      .get<void>(this.url + '/' + jobId + '/ManualJobAlert?suppliers=' + suppliers)
      .toPromise();
  }

  public changeDriverTruck(item: any): Promise<any> {
    return this.handleRequest(
      this.http.post(this.url + '/changeDriverTruck', item),
      this.handlersFactory.getForUpdate(this.entityTitle),
    ).then(this.extractData);
  }

  public getHistory(jobId: number): Promise<any> {
    return this.handleRequest(
      this.http.get(this.url + '/getHistory/' + jobId),
      this.handlersFactory.getForReadAll(),
    ).then(this.extractData);
  }

  public addHistory(item: any): Promise<any> {
    return this.handleRequest(
      this.http.post(this.url + '/addHistory', item),
      this.handlersFactory.getForUpdate(this.entityTitle),
    ).then(this.extractData);
  }

  public changeJobDetails(item: any): Promise<any> {
    return this.handleRequest(
      this.http.post(this.url + '/changeJobDetails', item),
      this.handlersFactory.getForUpdate(this.entityTitle),
    ).then(this.extractData);
  }

  public updateGrabDisabledState(
    request: ChangeGrabDisabledStateDto,
    skipUpdateMessage: boolean = false,
  ): Promise<any> {
    const responseHandler = skipUpdateMessage
      ? null
      : this.handlersFactory.getForUpdate(this.entityTitle);

    return this.handleRequest(
      this.http.put(this.url + '/grabDisabledState', request),
      responseHandler,
    );
  }

  public addDriverNotes(item: any): Promise<any> {
    return this.handleRequest(
      this.http.post(this.url + '/addDriverNotes', item),
      this.handlersFactory.getForUpdate(this.entityTitle),
    ).then(this.extractData);
  }

  public changeOrderDetail(item: any): Promise<any> {
    return this.handleRequest(
      this.http.post(this.url + '/changeOrderDetail', item),
      this.handlersFactory.getForUpdate(this.entityTitle),
    ).then(this.extractData);
  }

  public getDisputes(jobId: number): Promise<any> {
    return this.handleRequest(
      this.http.get(this.url + '/dispute/' + jobId),
      this.handlersFactory.getForReadAll(),
    ).then(this.extractData);
  }

  public getJobNumber(jobId: number): Promise<string> {
    return this.handleRequest(
      this.http.get(this.url + '/getjobnumber/' + jobId, { responseType: 'text' }),
      this.handlersFactory.getForReadAll(),
    );
  }

  public removeJobSupplier(item: IRemoveSupplier): Promise<boolean> {
    return this.handleRequest(
      this.http.post(this.url + '/removeJobSupplier', item),
      this.handlersFactory.getForUpdate(this.entityTitle),
    ).then(this.extractData);
  }

  public completeJob(item: any): Promise<any> {
    return this.handleRequest(
      this.http.post(this.url + '/completeJob', item),
      this.handlersFactory.getForUpdate(this.entityTitle),
    ).then(this.extractData);
  }

  public sendForApproval(item: any): Promise<any> {
    return this.handleRequest(
      this.http.post(this.url + '/sendForApproval', item),
      this.handlersFactory.getForUpdate(this.entityTitle),
    ).then(this.extractData);
  }

  public searchSuppliers(item: ISearchSupplier): Promise<any> {
    return this.handleRequest(
      this.http.post(this.url + '/searchSuppliers', item),
      this.handlersFactory.getForReadAll(),
    ).then(this.extractData);
  }

  public getPurchaseOrderNo(orderId: number): Promise<string> {
    return this.handleRequest(
      this.http.get(`${this.url}/purchaseorderno?orderId=${orderId}`, { responseType: 'text' }),
      this.handlersFactory.getForReadAll(),
    );
  }

  public saveTradePlate(item: any) {
    return this.handleRequest(
      this.http.post(this.url + '/saveTradePlate', item),
      this.handlersFactory.getForUpdate(this.entityTitle),
    ).then(this.extractData);
  }

  public isInvoiced(jobId: number): Promise<any> {
    return this.handleRequest(
      this.http.get(this.url + '/isInvoiced/' + jobId),
      this.handlersFactory.getForReadAll(),
    ).then(this.extractData);
  }

  public isCustomerInvoiced(jobId: number): Promise<any> {
    return this.handleRequest(
      this.http.get(this.url + '/isCustomerInvoiced/' + jobId),
      this.handlersFactory.getForReadAll(),
    ).then(this.extractData);
  }

  public isSupplierInvoiced(jobId: number): Promise<any> {
    return this.handleRequest(
      this.http.get(this.url + '/isSupplierInvoiced/' + jobId),
      this.handlersFactory.getForReadAll(),
    ).then(this.extractData);
  }

  public downloadPDF(uri: any) {
    console.log('Getting URI: ', uri);
    return this.http.get(uri, { responseType: 'blob' });
  }

  public getSupplierJobs(requestDTO): Promise<IProfileJobInfo[]> {
    return this.http
      .get<IProfileJobInfo[]>('api/job/getSupplierJobs', { params: requestDTO })
      .toPromise();
  }

  public getSupplierWatchedJobs(requestDTO): Promise<IProfileJobInfo[]> {
    return this.http
      .get<IProfileJobInfo[]>(`api/job/getSupplierWatchedJobs`, { params: requestDTO })
      .toPromise();
  }

  public searchJobsByRegFull(reg: string) {
    const params = this.constructHttpParams({ search: reg });
    return this.handleRequest(
      this.http.get<IProfileJobInfo[]>('api/job/search-by-reg-full-info', { params: params }),
      this.handlersFactory.getForReadAll(),
    );
  }

  public searchJobsByReg(reg: string) {
    const params = this.constructHttpParams({ search: reg });
    return this.handleRequest(
      this.http.get<IBasicJobData[]>('api/job/search-by-reg', { params: params }),
      this.handlersFactory.getForReadAll(),
    );
  }

  public getTaskTrackingInfo(jobId: number, taskId: number): Promise<ITaskTrackingDTO[]> {
    return this.http
      .get<ITaskTrackingDTO[]>(
        this.url + `/TaskTracking/${encodeURIComponent(jobId)}/${encodeURIComponent(taskId)}`,
      )
      .toPromise();
  }

  public getBinPrice(jobId: number): Promise<number> {
    return this.http.get<number>(`${this.url}/${jobId}/binPrice`).toPromise();
  }

  public patch(jobId: number, patch: Operation[]) {
    return this.http.patch(`api/job/` + jobId, patch).toPromise();
  }

  //Get element by Id
  public getForComponent<T>(id: number | string, component: string): Promise<T> {
    return this.http.get<T>(`/api/job/${id}/component/${component}`).toPromise();
  }

  getUpdateSupplierScheduleContext(jobId: number): Promise<JobSupplierScheduleContext> {
    return this.http
      .get<JobSupplierScheduleContext>(`/api/job/${jobId}/component/travel-schedule-context`)
      .toPromise();
  }

  public queueJobsForAutoDirect(jobIds: number[]): Promise<any> {
    return this.handleRequest(
      this.http.post(`${this.url}/queueAutoDirect`, jobIds),
      this.handlersFactory.getForUpdate(this.entityTitle),
      jobIds.length === 1
        ? 'Job queued for auto direct.'
        : `${jobIds.length} jobs queued for auto direct.`,
    );
  }
}
